import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { adminManagementApi, authApi } from '../services/api';
import { isEntraAuth, loginRequest } from '../auth/authConfig';

// Resolve the highest role a user holds across all DBs
const highestRole = (roles) => {
  const order = ['SysAdmin', 'AppAdmin', 'PowerUser', 'User', 'Guest'];
  for (const r of order) {
    if (Object.values(roles || {}).includes(r)) return r;
  }
  return 'Guest';
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Access level constants shared by both providers
const ACCESS_LEVELS = {
  'Guest': 0,
  'User': 1,
  'PowerUser': 2,
  'Admin': 3,
  'AppAdmin': 3,
  'SysAdmin': 4
};

// Shared access check helpers
const makeAccessHelpers = (currentUser) => {
  const isSysAdmin = currentUser?.isSysAdmin === true;

  // Derive isAdmin / isPowerUser from new roles dict (highest role across all DBs)
  const best = isSysAdmin ? 'SysAdmin' : highestRole(currentUser?.roles);
  const isAdmin = isSysAdmin || best === 'AppAdmin';
  const isPowerUser = isAdmin || best === 'PowerUser';
  const canEdit = (ACCESS_LEVELS[best] ?? 0) >= ACCESS_LEVELS['User'];
  const canConfigure = (ACCESS_LEVELS[best] ?? 0) >= ACCESS_LEVELS['PowerUser'];

  // access_Group for display (header, etc.)
  const access_Group = isSysAdmin ? 'SysAdmin' : best;

  // ─────────────────────────────────────────────────────────────────────────
  // roles + permissions dict key contract
  //
  // Both currentUser.roles and currentUser.permissions are keyed by the
  // database's slug (the AppSec_Databases.Slug column — "gwa01", "t1eg2").
  // This is the SAME string used as the URL :dbSlug route param.
  //
  // Backend source of truth: DatabaseInstanceExtensions.SlugKey() in the .NET
  // API. Any new backend endpoint that produces a similar dict MUST use it.
  //
  // Frontend callers of roleInDb / hasPermission MUST pass a slug (either
  // db.slug from an available-databases response, or the URL :dbSlug param).
  // NEVER pass display name, database name, or any other identifier.
  // ─────────────────────────────────────────────────────────────────────────

  // Role for a specific DB (by slug)
  const roleInDb = (dbSlug) => {
    if (isSysAdmin) return 'SysAdmin';
    return currentUser?.roles?.[dbSlug?.toLowerCase()] || 'None';
  };

  // Permission check for a specific DB slug (reads from resolved permissions)
  const hasPermission = (key, dbSlug) => {
    if (!key || !currentUser) return false;
    if (isSysAdmin) return true;
    const slug = dbSlug?.toLowerCase() || localStorage.getItem('pmd-selected-db-slug') || '';
    return currentUser.permissions?.[slug]?.includes(key) ?? false;
  };

  // Backward-compat: canAccessModule checks module.* permission keys
  const canAccessModule = (moduleName) => {
    if (!currentUser) return false;
    if (isAdmin) return true;
    const keyMap = {
      'submittal': 'module.submittal',
      'estimating': 'module.estimating',
      'integration': 'module.integration',
      'purchasing': 'module.purchasing',
      'transmittal': 'module.transmittal',
      'qaqc': 'module.qaqc',
      'commissioning': 'module.commissioning',
      'sub_contractor': 'module.subcontractor',
      'subcontractor': 'module.subcontractor'
    };
    const permKey = keyMap[moduleName.toLowerCase()];
    if (!permKey) return false;
    // Check any DB (for backward compat)
    return Object.values(currentUser.permissions || {}).some(perms => perms.includes(permKey));
  };

  const hasAccessLevel = (requiredLevel) => {
    if (!currentUser) return false;
    const userLevel = ACCESS_LEVELS[best] ?? 0;
    const required = ACCESS_LEVELS[requiredLevel] ?? 0;
    return userLevel >= required;
  };

  return { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel, roleInDb, hasPermission, access_Group };
};

// Dev VM hostname → developer email (local dev auto-identity)
const DEV_IP_MAP = {
  'localhost':       'josh.neville@wmeng.com',
  '127.0.0.1':      'josh.neville@wmeng.com',
  '192.168.249.100': 'josh.neville@wmeng.com',
  '192.168.249.101': 'danny.elliott@wmeng.com',
  '192.168.249.102': 'todd.mcmurry@wmeng.com',
  '192.168.249.103': 'jared.mcirvin@wmeng.com',
};

// ============================================================
// Impersonation Auth Provider (existing behavior, unchanged)
// ============================================================
export const ImpersonationAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  // "myIdentity" = who you ARE on this machine (persisted via green check)
  const [myIdentity, setMyIdentity] = useState(null);
  const [hasPersistedIdentity, setHasPersistedIdentity] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvePermsForUser = useCallback(async (user) => {
    try {
      const res = await adminManagementApi.resolveAllPermissions(user.id);
      const data = res.data || {};
      return { ...user, roles: data.roles || {}, permissions: data.permissions || {} };
    } catch {
      return { ...user, roles: {}, permissions: {} };
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminManagementApi.getUsers();
      const rawUsers = res.data || [];
      const allUsers = rawUsers.map(u => ({
        id: u.id,
        user_Name: u.displayName,
        email: u.email,
        azureObjectId: u.azureObjectId,
        isSysAdmin: u.isSysAdmin === true,
        isActive: u.isActive,
        organizationId: u.organizationId,
        organizationName: u.organizationName,
        organizationLogoUrl: u.organizationLogoUrl || null,
        roles: {},
        permissions: {}
      }));
      setUsers(allUsers);

      // 1. Check for persisted identity ("This is me" — green check)
      const identityId = localStorage.getItem('pmd-identity');
      if (identityId) {
        const identityUser = allUsers.find(u => u.id === parseInt(identityId, 10));
        if (identityUser) {
          const withPerms = await resolvePermsForUser(identityUser);
          setMyIdentity(withPerms);
          setCurrentUser(withPerms);
          setHasPersistedIdentity(true);
          setLoading(false);
          return;
        }
      }

      // 2. No persisted identity — try to auto-detect by dev VM hostname
      const hostname = window.location.hostname;
      const devEmail = DEV_IP_MAP[hostname];
      if (devEmail) {
        const devUser = allUsers.find(u => u.email?.toLowerCase() === devEmail.toLowerCase());
        if (devUser) {
          const withPerms = await resolvePermsForUser(devUser);
          setMyIdentity(withPerms);
          setCurrentUser(withPerms);
          setLoading(false);
          return;
        }
      }

      // 3. No IP match — fall back to first SysAdmin then first user
      const sysAdmin = allUsers.find(u => u.isSysAdmin);
      const fallback = sysAdmin || allUsers[0] || null;
      if (fallback) {
        const withPerms = await resolvePermsForUser(fallback);
        setMyIdentity(withPerms);
        setCurrentUser(withPerms);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [resolvePermsForUser]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Impersonate — temporarily view as another user (resolves their permissions)
  const impersonate = async (user) => {
    const withPerms = await resolvePermsForUser(user);
    setCurrentUser(withPerms);
    setIsImpersonating(withPerms?.id !== myIdentity?.id);
  };

  // Stop impersonating — revert to my identity
  const stopImpersonating = () => {
    setCurrentUser(myIdentity);
    setIsImpersonating(false);
  };

  // Claim identity — "This is me" (green check)
  const claimIdentity = async (user) => {
    const target = user || currentUser;
    const withPerms = target.roles ? target : await resolvePermsForUser(target);
    setMyIdentity(withPerms);
    setCurrentUser(withPerms);
    setIsImpersonating(false);
    setHasPersistedIdentity(true);
    if (withPerms?.id) {
      localStorage.setItem('pmd-identity', withPerms.id.toString());
    }
  };

  const { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel, roleInDb, hasPermission, access_Group } = makeAccessHelpers(currentUser);

  useEffect(() => {
    window.__dawnDebugAuth = currentUser ? {
      name: currentUser.displayName || currentUser.user_Name || currentUser.email,
      email: currentUser.email,
      isSysAdmin: currentUser.isSysAdmin,
      roles: currentUser.roles || {},
      permissions: currentUser.permissions || {},
      isImpersonating,
      realName: isImpersonating ? (myIdentity?.displayName || myIdentity?.email) : null,
    } : null;
  }, [currentUser, isImpersonating, myIdentity]);

  const authStep = loading ? 'connecting' : 'ready';

  const value = {
    currentUser,
    realUser: myIdentity,
    isImpersonating,
    hasPersistedIdentity,
    users,
    loading,
    accessDenied: false,
    authStep,
    isSysAdmin,
    isAdmin,
    isPowerUser,
    canEdit,
    canConfigure,
    impersonate,
    stopImpersonating,
    claimIdentity,
    canAccessModule,
    hasAccessLevel,
    roleInDb,
    hasPermission,
    access_Group,
    refreshUsers: loadUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// Entra Auth Provider (Azure AD / MSAL)
// ============================================================
export const EntraAuthProvider = ({ children }) => {
  // These hooks are safe here because EntraAuthProvider is only rendered inside MsalProvider
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [currentUser, setCurrentUser] = useState(null);
  const [realUser, setRealUser] = useState(null); // The actual logged-in user (never changes during impersonation)
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Trigger login redirect if not authenticated AND no interaction in progress
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch((err) => {
        console.error('Login redirect failed:', err);
      });
    }
  }, [isAuthenticated, inProgress, instance]);

  // Once authenticated, resolve identity via /api/auth/me
  const resolveUser = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) return;

    try {
      const storedImpersonation = localStorage.getItem('pmd-impersonated-user');

      // Fire both in parallel — they're independent of each other
      const [usersResult, meResult] = await Promise.allSettled([
        adminManagementApi.getUsers(),
        authApi.getMe(),
      ]);

      // Process users list (non-fatal if it fails)
      const rawUsers = usersResult.status === 'fulfilled' ? (usersResult.value.data || []) : [];
      const allUsers = rawUsers.map(u => ({
        id: u.id,
        user_Name: u.displayName,
        access_Group: u.isSysAdmin ? 'SysAdmin' : 'User',
        email: u.email,
        azureObjectId: u.azureObjectId,
        isSysAdmin: u.isSysAdmin === true,
        isActive: u.isActive
      }));
      setUsers(allUsers);

      // Process identity
      let meData = null;
      if (meResult.status === 'fulfilled') {
        meData = meResult.value.data;
        console.log('[Auth] /api/auth/me resolved:', meData?.displayName, 'isSysAdmin:', meData?.isSysAdmin);
      } else {
        console.error('[Auth] /api/auth/me FAILED — user will fall back to Guest:', meResult.reason?.message || meResult.reason);
      }

      // Check if backend says access denied (user not in AppSec_Users)
      if (meData && meData.accessDenied) {
        console.warn('[Auth] Access denied — user not in AppSec_Users. Email:', meData.email);
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // If /api/auth/me returned a user, use AppSec_Users as authority
      if (meData && meData.id != null) {
        setAccessDenied(false);
        const resolvedUser = {
          id: meData.id,
          user_Name: meData.displayName,
          isSysAdmin: meData.isSysAdmin ?? false,
          email: meData.email,
          azureObjectId: meData.azureObjectId,
          groups: meData.groups,
          roles: meData.roles || {},
          permissions: meData.permissions || {}
        };

        // Always set realUser so impersonation can reference the actual logged-in user
        setRealUser(resolvedUser);

        // Check if admin is impersonating someone
        const resolvedHelpers = makeAccessHelpers(resolvedUser);
        if (resolvedHelpers.isAdmin && storedImpersonation) {
          const impersonated = allUsers.find(u => u.id === parseInt(storedImpersonation, 10));
          if (impersonated) {
            try {
              const permRes = await adminManagementApi.resolveAllPermissions(impersonated.id);
              const pd = permRes.data || {};
              setCurrentUser({ ...impersonated, roles: pd.roles || {}, permissions: pd.permissions || {} });
            } catch {
              setCurrentUser({ ...impersonated, roles: {}, permissions: {} });
            }
            setIsImpersonating(true);
            setLoading(false);
            return;
          }
        }
        setCurrentUser(resolvedUser);
      } else {
        // /api/auth/me call failed (network, cold start, token timing) — don't deny,
        // user is still Entra-authenticated. They'll land as a guest until next resolveUser.
        console.warn('[Auth] /api/auth/me returned no valid user — leaving as guest, not denying');
      }
    } catch (err) {
      // Transient error (network, API unavailable) — same: don't bounce an authenticated user
      console.warn('[Auth] resolveUser threw unexpectedly — leaving as guest:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    resolveUser();
  }, [resolveUser]);

  const impersonate = async (user) => {
    // Only SysAdmins can impersonate in Entra mode
    const helpers = makeAccessHelpers(realUser || currentUser);
    if (!helpers.isSysAdmin) return;

    try {
      const permRes = await adminManagementApi.resolveAllPermissions(user.id);
      const pd = permRes.data || {};
      const withPerms = { ...user, roles: pd.roles || {}, permissions: pd.permissions || {} };
      setCurrentUser(withPerms);
    } catch {
      setCurrentUser({ ...user, roles: {}, permissions: {} });
    }
    setIsImpersonating(true);
    if (user?.id) {
      localStorage.setItem('pmd-impersonated-user', user.id.toString());
    }
  };

  const stopImpersonating = () => {
    setCurrentUser(realUser);
    setIsImpersonating(false);
    localStorage.removeItem('pmd-impersonated-user');
  };

  const { isSysAdmin, isAdmin, isPowerUser, canEdit, canConfigure, canAccessModule, hasAccessLevel, roleInDb, hasPermission, access_Group } = makeAccessHelpers(currentUser);

  useEffect(() => {
    window.__dawnDebugAuth = currentUser ? {
      name: currentUser.displayName || currentUser.user_Name || currentUser.email,
      email: currentUser.email,
      isSysAdmin: currentUser.isSysAdmin,
      roles: currentUser.roles || {},
      permissions: currentUser.permissions || {},
      isImpersonating,
      realName: isImpersonating ? (realUser?.displayName || realUser?.email) : null,
    } : null;
  }, [currentUser, isImpersonating, realUser]);

  const authStep = !isAuthenticated
    ? (inProgress !== InteractionStatus.None ? 'authenticating' : 'connecting')
    : loading ? 'verifying' : 'ready';

  const value = {
    currentUser,
    realUser,
    isImpersonating,
    users,
    loading,
    accessDenied,
    authStep,
    isSysAdmin,
    isAdmin,
    isPowerUser,
    canEdit,
    canConfigure,
    impersonate,
    stopImpersonating,
    canAccessModule,
    hasAccessLevel,
    roleInDb,
    hasPermission,
    access_Group,
    refreshUsers: resolveUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// Default AuthProvider - picks based on AUTH_MODE
// (kept for backward compatibility but App.js uses specific ones)
// ============================================================
export const AuthProvider = ({ children }) => {
  if (isEntraAuth()) {
    return <EntraAuthProvider>{children}</EntraAuthProvider>;
  }
  return <ImpersonationAuthProvider>{children}</ImpersonationAuthProvider>;
};

export default AuthContext;
